import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Mail, MessageSquare, Search, Send } from "lucide-react";
import { toast } from "sonner";
import { apiClient, formatApiError } from "@/lib/auth";
import { useOps } from "@/lib/opsCards";
import { inputCls } from "./FormRenderer";

const GROUPS = [["all", "Everyone"], ["contact", "Contacts"], ["staff", "Staff"], ["pilot", "Pilot requests"]];
const chip = (on) => `px-2.5 py-1 rounded-full border font-mono text-[0.55rem] tracking-[0.14em] uppercase transition-colors ${on ? "border-brand-cyan/60 bg-brand-cyan/10 text-brand-cyan" : "border-slate-700 text-slate-400 hover:text-slate-200"}`;

function Progress({ jobId, onDone }) {
  const [job, setJob] = useState(null);
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await apiClient.get(`/admin/messages/jobs/${jobId}`);
        if (stop) return;
        setJob(data);
        if (data.status === "done") { onDone(); return; }
      } catch { /* retry */ }
      setTimeout(tick, 1500);
    };
    tick();
    return () => { stop = true; };
  }, [jobId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!job) return null;
  const smsDone = job.sms_sent + job.sms_failed + (job.sms_skipped || 0);
  const texts = `${job.sms_sent} text${job.sms_sent === 1 ? "" : "s"} sent${job.sms_skipped ? `, ${job.sms_skipped} skipped (SMS off)` : ""}${job.sms_failed ? `, ${job.sms_failed} failed` : ""}`;
  return (
    <div data-testid="ops-msg-progress" className="rounded-md border border-brand-cyan/40 bg-brand-cyan/5 p-3 text-xs text-slate-200 space-y-1">
      <div className="font-mono text-[0.55rem] tracking-[0.18em] uppercase text-brand-cyan">{job.status === "done" ? "Done" : "Sending…"}</div>
      {job.sms_total > 0 && <div data-testid="ops-msg-progress-sms">SMS {smsDone}/{job.sms_total} · {texts}</div>}
      {job.email_total > 0 && <div data-testid="ops-msg-progress-email">Email {job.email_sent + job.email_failed}/{job.email_total} · {job.email_sent} sent{job.email_failed ? ` · ${job.email_failed} failed` : ""}</div>}
      {job.skipped > 0 && <div className="text-slate-400">{job.skipped} skipped (no number/email for the chosen channel)</div>}
    </div>
  );
}

// Card popup panel: tick people, pick SMS / Email / Both, personalise with {first_name} / {organization}.
export default function MessageCenter() {
  const { reload } = useOps();
  const [people, setPeople] = useState([]);
  const [group, setGroup] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(() => new Set());
  const [channels, setChannels] = useState({ sms: true, email: false });
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [jobId, setJobId] = useState(null);
  useEffect(() => { apiClient.get("/admin/messages/recipients").then(({ data }) => setPeople(data.recipients || [])).catch(() => toast.error("Could not load people")); }, []);

  const shown = useMemo(() => people.filter((p) => (group === "all" || p.group === group)
    && (!q || `${p.name} ${p.organization} ${p.email} ${p.phone}`.toLowerCase().includes(q.toLowerCase()))), [people, group, q]);
  const toggle = (k) => setSel((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });
  const allShown = shown.length > 0 && shown.every((p) => sel.has(p.key));
  const picked = people.filter((p) => sel.has(p.key));
  const reach = { sms: picked.filter((p) => p.phone).length, email: picked.filter((p) => p.email).length };

  const send = async () => {
    const ch = Object.keys(channels).filter((k) => channels[k]);
    if (!picked.length) return toast.error("Pick at least one person");
    if (!ch.length) return toast.error("Choose SMS, Email or both");
    if (!message.trim()) return toast.error("Write a message");
    if (channels.email && !subject.trim()) return toast.error("Add an email subject");
    if (!window.confirm(`Send to ${picked.length} people — ${channels.sms ? `${reach.sms} SMS` : ""}${channels.sms && channels.email ? " + " : ""}${channels.email ? `${reach.email} emails` : ""}?`)) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/admin/messages/send", { keys: picked.map((p) => p.key), channels: ch, subject, message });
      setJobId(data.id); reload();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="ops-message-center" className="space-y-3">
      <div className="flex flex-wrap gap-1.5">
        {GROUPS.map(([g, l]) => <button key={g} type="button" onClick={() => setGroup(g)} data-testid={`ops-msg-group-${g}`} className={chip(group === g)}>{l}</button>)}
      </div>
      <div className="relative">
        <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, org, phone, email…" data-testid="ops-msg-search" className={`${inputCls} pl-8`} />
      </div>
      <div className="rounded-md border border-slate-800 max-h-56 overflow-y-auto">
        <label className="flex items-center gap-2 px-3 py-2 border-b border-slate-800 text-xs text-slate-400 sticky top-0 bg-navy-950">
          <input type="checkbox" checked={allShown} onChange={() => setSel((s) => { const n = new Set(s); shown.forEach((p) => (allShown ? n.delete(p.key) : n.add(p.key))); return n; })} data-testid="ops-msg-select-all" className="accent-cyan-400" />
          Select all shown ({shown.length}) · {sel.size} picked
        </label>
        {shown.map((p) => (
          <label key={p.key} data-testid={`ops-msg-person-${p.key}`} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-200 hover:bg-white/[0.03] cursor-pointer">
            <input type="checkbox" checked={sel.has(p.key)} onChange={() => toggle(p.key)} className="accent-cyan-400" />
            <span className="truncate flex-1">{p.name}{p.organization && <span className="text-slate-500 text-xs"> · {p.organization}</span>}</span>
            {p.phone && <MessageSquare className="w-3.5 h-3.5 text-emerald-300" aria-label="has phone" />}
            {p.email && <Mail className="w-3.5 h-3.5 text-sky-300" aria-label="has email" />}
          </label>
        ))}
        {!shown.length && <div className="p-4 text-center text-xs text-slate-500">No people match.</div>}
      </div>
      <div className="flex gap-2">
        {[["sms", "SMS", MessageSquare], ["email", "Email", Mail]].map(([k, l, I]) => (
          <button key={k} type="button" onClick={() => setChannels((c) => ({ ...c, [k]: !c[k] }))} data-testid={`ops-msg-channel-${k}`} aria-pressed={channels[k]}
            className={`flex-1 inline-flex items-center justify-center gap-1.5 py-2 rounded-md border text-sm transition-colors ${channels[k] ? "border-brand-cyan/60 bg-brand-cyan/10 text-brand-cyan" : "border-slate-700 text-slate-400"}`}>
            <I className="w-4 h-4" /> {l} <span className="text-xs opacity-70">({reach[k]})</span>
          </button>
        ))}
      </div>
      {channels.email && <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject" data-testid="ops-msg-subject" className={inputCls} />}
      <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Hi {first_name}, …" data-testid="ops-msg-body" className={inputCls} />
      <div className="text-[0.65rem] text-slate-500">Use <code>{"{first_name}"}</code>, <code>{"{name}"}</code> or <code>{"{organization}"}</code> — each person gets their own. SMS is paced ~1 every 3s.</div>
      {jobId && <Progress key={jobId} jobId={jobId} onDone={() => toast.success("Messages finished sending")} />}
      <button type="button" onClick={send} disabled={busy} data-testid="ops-msg-send"
        className="w-full inline-flex items-center justify-center gap-2 py-2.5 rounded-md bg-gradient-to-r from-brand-blue to-brand-cyan text-navy-950 font-mono text-[0.65rem] font-bold tracking-[0.16em] uppercase hover:brightness-110 disabled:opacity-60">
        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Send
      </button>
    </div>
  );
}
